import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  PerformancePeerAssignmentsReviewEvaluationFragment,
  PerformancePeerAssignmentsTargetFragment,
  PerformanceReviewProcessTargetForPeerAssignmentDocument,
  RemovePeersPerformanceReviewProcessTargetDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

interface Props {
  processTargetId: number
  processTargetAccess: PerformancePeerAssignmentsTargetFragment
  currentPeerEvaluations: PerformancePeerAssignmentsReviewEvaluationFragment[]
  currentPeerEmployeeIds: number[]
  onClose: () => void
}

const RemovePeersModal = ({
  processTargetId,
  processTargetAccess,
  currentPeerEvaluations,
  currentPeerEmployeeIds,
  onClose,
}: Props) => {
  const { reviewId } = useParams()
  const [selectedEmployees, setSelectedEmployees] = React.useState<
    EmployeeForSelectFragmentNoAccess[]
  >([])

  const [removePeersToPerformanceProcess, { loading: adding }] = useMutation(
    RemovePeersPerformanceReviewProcessTargetDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.removePeerEvaluationsPerformanceReviewProcessTarget,
      refetchQueries: [PerformanceReviewProcessTargetForPeerAssignmentDocument],
    }
  )

  const submit = async () => {
    const response = await removePeersToPerformanceProcess({
      variables: {
        evaluationIds: currentPeerEvaluations
          .filter(
            (e) =>
              e.reviewerEmployeeName?.id &&
              selectedEmployees.some((emp) => emp.id === e.reviewerEmployeeName?.id)
          )
          .map((ev) => ev.id),
        id: `${reviewId}-${processTargetId}`,
      },
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    onClose()
  }

  const handleEmployeeSelection = (employees: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(employees)
  }

  return (
    <Modal
      title={i18n.t('performance_process.reviews.peer_management.remove_peers_for', {
        target_name: processTargetAccess.employeeName?.fullName,
      })}
      illustration="user-male-female"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <Button
          submit
          disabled={!selectedEmployees.length || adding}
          type="primary"
          aria-label={i18n.t('performance_process.reviews.peer_management.remove_peers')}
        >
          {i18n.t('performance_process.reviews.peer_management.remove_peers')}
        </Button>
      }
    >
      <EmployeeSelector
        multiple
        alwaysOpen
        value={selectedEmployees}
        onSelect={handleEmployeeSelection}
        employeeIds={currentPeerEmployeeIds}
        excludeIds={[processTargetId]}
        includeInactive
      />
    </Modal>
  )
}

export default observer(RemovePeersModal)
